import { Link } from "react-router-dom";
import { FlaskConical, CheckCircle2, CalendarCheck, ShieldCheck } from "lucide-react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import CtaBand from "@/components/CtaBand";
import { Reveal, Eyebrow, SectionTitle } from "@/components/Reveal";

const FACTORS = [
  "Your symptoms, if any",
  "Type of sexual exposure",
  "Time since exposure",
  "Anatomical site involved",
  "Previous test results",
  "Clinical assessment",
];

const TESTS = [
  { name: "HIV", desc: "Modern antigen/antibody testing, timed correctly after exposure" },
  { name: "Syphilis", desc: "VDRL and TPHA for screening and confirmation" },
  { name: "Gonorrhea", desc: "Gram stain and PCR swab" },
  { name: "Chlamydia", desc: "Simple urine or swab-based PCR" },
  { name: "Warts", desc: "Biopsy" },
  { name: "Herpes", desc: "Tzanck smear and PCR" },
  { name: "Hepatitis B", desc: "Blood tests; vaccination advice where relevant" },
  { name: "Hepatitis C", desc: "Blood testing when clinically indicated" },
  { name: "Other STI tests", desc: "When clinically indicated by your assessment" },
];

const Testing = () => (
  <>
    <SEO
      title="STD & HIV Testing in Delhi — Confidential Testing Advice | Dr. Ankur Lal"
      description="Confidential STD & HIV testing guidance in Delhi. The right tests depend on your symptoms, exposure and timing — get individualized testing advice from Dr. Ankur Lal at Delhi STD Clinic."
      path="/std-hiv-testing"
    />
    <PageHero
      eyebrow="Confidential Testing — Delhi"
      title="STD & HIV Testing in Delhi"
      intro="The right test, at the right time, interpreted correctly. Testing advice at Delhi STD Clinic is individualized — never one-size-fits-all, never unnecessary."
    />

    <section className="py-14 sm:py-20" data-testid="testing-factors">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-start">
        <Reveal>
          <Eyebrow>Individualized Testing</Eyebrow>
          <SectionTitle className="mt-4">Which tests are appropriate depends on</SectionTitle>
          <ul className="mt-7 space-y-3.5">
            {FACTORS.map((f) => (
              <li key={f} className="flex items-start gap-3 text-slate-700">
                <CheckCircle2 className="w-5 h-5 text-emerald-800 mt-0.5 shrink-0" />
                <span className="text-[15px] leading-relaxed">{f}</span>
              </li>
            ))}
          </ul>
          <div className="mt-8 bg-secondary border border-amber-600/20 rounded-2xl p-6">
            <p className="text-sm text-slate-700 leading-relaxed">
              <span className="font-semibold text-slate-950">A note on responsible testing:</span>{" "}
              we do not promote unnecessary testing. Your panel is chosen based on clinical
              assessment — so you test for what matters, at the right time, and nothing more.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="bg-white border border-slate-900/10 rounded-3xl p-7 sm:p-9">
            <div className="flex items-center gap-2.5">
              <FlaskConical className="w-5 h-5 text-emerald-900" />
              <h2 className="font-serif text-2xl font-semibold text-slate-950">
                Tests that may be considered
              </h2>
            </div>
            <div className="mt-6 space-y-4">
              {TESTS.map((t) => (
                <div key={t.name} className="flex items-start gap-4 border-b border-slate-100 pb-4 last:border-0 last:pb-0">
                  <span className="w-2 h-2 rounded-full bg-amber-600 mt-2 shrink-0" />
                  <div>
                    <p className="font-semibold text-slate-900 text-[15px]">{t.name}</p>
                    <p className="text-sm text-slate-500 mt-0.5">{t.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>

    <section className="pb-16 sm:pb-24" data-testid="testing-cta-section">
      <div className="max-w-4xl mx-auto px-5 sm:px-8">
        <Reveal>
          <div className="bg-emerald-950 text-emerald-50 rounded-3xl p-8 sm:p-12 text-center relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-emerald-700/25 blur-3xl" />
            <ShieldCheck className="w-8 h-8 mx-auto text-amber-400" />
            <h2 className="relative mt-4 font-serif text-3xl sm:text-4xl font-semibold text-white">
              Not sure what to test for, or when?
            </h2>
            <p className="relative mt-4 text-emerald-100/80 leading-relaxed max-w-xl mx-auto">
              Get confidential testing advice from Dr. Ankur Lal — online or in clinic —
              based on your specific exposure and situation.
            </p>
            <Link
              to="/booking?type=online"
              data-testid="testing-advice-cta"
              className="relative mt-7 inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold text-sm px-8 py-4 rounded-full transition-all shadow-lg hover:-translate-y-0.5"
            >
              <CalendarCheck className="w-4 h-4" /> Get Testing Advice
            </Link>
          </div>
        </Reveal>
      </div>
    </section>

    <CtaBand />
  </>
);

export default Testing;
